/**
 * Ein Stück steht: frisch anlaufen statt still warten. (05.10.2026)
 *
 * ## Der Anlass
 *
 * Martin, 05.10.: „Er liest auch nicht immer den gesamten Text vor … hat er gesagt nein und der restliche Text wurde
 * nicht vorgelesen.“ Journal 10:55 (iPhone-App, PCM-Schalter an):
 *
 *     10:55:28.40  [sichtbar-d] hidden            (0,33 s lang — genau im Augenblick des ersten Tons)
 *     10:55:28.58  [tts-pcm] datei grund=kontext-weckfrist … weckMs=20     der Web-Audio-Kontext wacht hinten nicht auf
 *     10:55:28.59  play() der Datei auf der versteckten Seite (die Datei wird am Server noch gerechnet)
 *     10:55:28.74  [sichtbar-d] visible
 *     10:55:30.73  [tts-pcm] tippen-zum-weiterspielen grund=rueckkehr      statt eines frischen Starts: ein Hinweis
 *     10:55:31.63  [tts-start] steht sicht=hidden jetzt=visible … rs=0 el=spielt
 *     10:55:32.05  [tts-t] seg=0 …                 die Datei ist da, `play()` wird erfüllt — der Hinweis geht weg
 *     10:55:43.56  [tts-gap] via=abbruch:pille audio=-1.00s echt=14.95s    Martin tippt die Pille
 *
 * Das Stück war 4,5 s lang und endete in den 11,5 s nach 10:55:32 nicht. Was in dieser Zeit zu hören war, steht nicht
 * im Journal (Martin: „hat er gesagt nein“): ob das Element gar nicht klang, nach dem ersten Wort stehen blieb oder
 * nur sein Ende verlor, war am 05.10. nicht messbar — die Zeile schrieb `audio=-1.00s … rs=0`, weil der Abbruch die
 * Quelle schon entladen hatte. Seither steht die Stelle in `[tts-gap] … ct=`.
 *
 * Drei Dinge griffen ineinander (tts-queue.ts, `playOne`, vor diesem Stand):
 *  1. Für ein Stück nach einer PCM-Unterbrechung stand bei „steht nach der Rückkehr“ ein HINWEIS statt des frischen
 *     Starts, der seit Ticket 2547 für alle anderen Stücke gilt und im Journal jedes Mal half (02.10. 08:05,
 *     03.10. 17:41, 04.10. 13:20 und 14:56: `via=stillstand`, danach `via=ended`).
 *  2. Der Hinweis rief `play()` auf einem Element, das schon „spielt“ meldet — das tut nichts.
 *  3. `playing` nahm den Hinweis zurück. Das Ereignis kommt, sobald Daten da sind und das Element nicht pausiert
 *     ist; ob Ton herauskommt, sagt es nicht. Danach prüfte nichts mehr, bis der Wachhund das Stück nach Dauer + 10 s
 *     übersprang (und als gesprochen zählte).
 *
 * ## Die Regel
 *
 * Nur vorrückende Zeit beweist Ton. Steht ein Stück bei sichtbarer Seite, ohne Nutzer-Pause,
 *  - 2 s nach der Rückkehr in den Vordergrund (wie bisher, jetzt für JEDES Stück), oder
 *  - NUR IN DER APP: 3 s lang, während sein Ton abspielbar vorliegt — von Anfang an oder mittendrin (gezählt ab dem
 *    Augenblick, in dem der Ton DA ist, nicht ab `play()`: ein Stück, dessen Datei gerade erst ankam, steht nicht;
 *    Nachladen ist kein Stillstand; und steht es kurz vor seinem Ende, ging nur das `ended` verloren — dann gilt es
 *    als zu Ende),
 * endet das Element und dasselbe Stück läuft auf einem frischen an — ab der Stelle, an der es stand, wenn schon
 * etwas zu hören war (nichts doppelt; nur bei Adressen, die vom Server geholt werden, `wiederanlaufAdresse`),
 * sonst von seinem Anfang. Höchstens `ANLAUF_MAX`-mal HINTEREINANDER ohne Fortgang; steht es dann immer noch, hält
 * es an und fragt sichtbar („Tippen zum Weiterspielen“) — ein Tipp gibt ihm wieder einen frischen Spieler.
 * Übersprungen wird es nicht. Ein Anlauf, der hörbar über alles bisher Erreichte hinauskam, war ein Erfolg: der
 * nächste Hänger zählt wieder ab eins (`anlaufZaehltNeu`).
 *
 * ⚠️ Prüfung 06.10.: Die 3-s-Regel gilt NICHT im Browser (Mac). Dort maß der Bericht in 68 Wiedergaben keinen
 *    einzigen Stillstand, und die Regel überstimmte eine Pause von außen (Medientaste, AirPods heraus): nach 3 s
 *    spielte das Element wieder (WebKit-Probe `[tts-neu] grund=mitte … el=pausiert`). Vorgabe „Mac-Browser
 *    unverändert“ — dort bleibt der Stand von main, nur der Anlauf nach der Rückkehr kam dazu (Ticket 2547).
 *
 * Die Frage wartet vorn nicht ewig: Kommt in `HINWEIS_WARTET_MAX_MS` sichtbarer Zeit keine Berührung, endet das
 * Vorlesen mit dem Abbruch `kein-tipp`. Solange die Schlange wartet, gilt die Seite als „spielt“ — die Tagesaufnahme
 * bliebe pausiert (ton-rekorder-schonung.ts) und das Wach-Wort ruhte (seiten-ton.ts), bis jemand das Telefon anfasst.
 * Der Text steht weiter in der Blase; ihr Knopf liest ihn vor.
 *
 * ⚠️ Hinten (Seite versteckt) entscheidet diese Regel nichts — dort bleibt alles wie vorher.
 *    Und dort liegt der größere Teil: Journal 29.09.–05.10. (Konto 1, 660 Wiedergaben) — 30 Stücke endeten erst über
 *    den Wachhund, 24 davon bei versteckter Seite (`[sichtbar-d]`; etwa 05.10. 12:04–12:06: sechs Stücke einer
 *    Antwort hintereinander, die App lag 108 s hinten, die Bilanz schrieb trotzdem `gesprochen=556`). Vorn waren es
 *    4, dazu 3 Abbrüche nach einem Stillstand und der Vorfall von 10:55 — das ist der Teil, den diese Regel trägt.
 *    Hinten fehlt ein Spieler, der dort klingt: der native Vorleser ruht seit 24.09. (vorlesen-nativ.ts, Ticket 2547).
 *    Seit heute steht ein solches Stück wenigstens wahr in der Bilanz (`verloren=`, nicht mehr `gesprochen=`).
 * ⚠️ Die Lehre von pre.3278 (24.09.): ein Wiederanlauf wurde damals nur mit nachgestelltem Element geprüft und brach
 *    das Vorlesen. Dieser hier läuft in tests/browser/vorlesen-haengt-webkit.mts mit echtem `<audio>` und echtem
 *    Web-Audio-Kontext in WebKit; das iPhone selbst ersetzt das nicht.
 */

/**
 * Gerät-Schalter zum Zurücknehmen: „0“ = aus. Dann läuft nichts mehr von selbst frisch an — es gilt wieder der Weg
 * von pre.3636 (nach einer PCM-Unterbrechung der Hinweis, sonst einmal von vorn, darunter der Wachhund).
 *
 * ⚠️ Der Schalter nimmt NUR das Anlaufen zurück. Was unabhängig davon berichtigt ist, bleibt: der Hinweis geht erst,
 *    wenn die Zeit vorrückt (und wartet vorn höchstens `HINWEIS_WARTET_MAX_MS`), die Pille stoppt nicht, während sie
 *    wartet, und die Bilanz zählt den gehörten Anteil. Wer auch das zurückhaben will, nimmt den Commit zurück.
 */
export const WIEDERANLAUF_SCHLUESSEL = "voice-v8.vorlesen-wiederanlauf";

/** So oft hintereinander läuft EIN Stück frisch an, ohne dass ein Anlauf vorankam, bevor es anhält und fragt. */
export const ANLAUF_MAX = 2;

/**
 * 15.10.2026 — VOM SYSTEM ANGEHALTEN: an der Stelle weiter, nicht erst nach 3 s frisch. (nur in der App)
 *
 * Martin, 15.10. 21:22: „bricht die Stimme nach etwa 20 Sekunden mitten im Satz ab, blendet aus und versucht es immer
 * wieder“. Journal 21:20:49–21:21:32 (Mikro beim Vorlesen offen, Datei-Weg): jedes neue `<audio>` lief an, rund 1 s
 * später meldete der Mikrofon-Motor `unterbrochen` (Tonsitzung), und das Element stand `el=pausiert` — niemand hatte
 * pausiert. Die 3-s-Regel wartete dann 3 s, baute einen frischen Spieler, der wieder ~1 s lief und wieder stand:
 * 4 Anläufe, 14 s Stille in EINEM Stück, das erste Stück (83 Zeichen) ging ganz verloren (`play()` mit AbortError
 * abgewiesen = als Fehler gezählt). Belegt 12.–15.10. (alle Konten): auf dem Datei-Weg folgte bei 71 von 2 649 Stücken
 * binnen 2 s ein `unterbrochen` (Martin am 14.10.: 46 von 479), auf dem PCM-Weg bei 2 von 327; alle 24 `[tts-neu]`
 * dieser Tage lagen auf dem Datei-Weg.
 *
 * Jetzt: Steht das Element `paused`, obwohl weder die Schlange noch ein Abbruch es anhielt, ruft der Takt (≤ 0,5 s)
 * `play()` auf DEMSELBEN Element — es spielt an der Stelle weiter, ohne Neuladen. Höchstens `WEITER_MAX`-mal je Lauf;
 * danach (oder wenn `play()` nicht hilft) greift wie bisher die 3-s-Regel mit frischem Spieler.
 * ⚠️ Nur in der App: im Browser ist ein `pause` von außen (Medientaste, AirPods heraus) gewollt und bleibt.
 */
export const WEITER_MAX = 3;

/**
 * 17.10.2026 — SOFORT WEITER: auf das `pause`-Ereignis des Elements, nicht erst im nächsten Takt (≤ 0,5 s). (nur App)
 *
 * Untersuchung 17.10. (Martin, „Hey Vespera“ aus): 504 Unterbrechungen der Tonsitzung an einem Tag, 432 davon beim
 * Abspielen (`[mikro-motor] unterbrochen`, `audio nein`); WebKit hält dabei das `<audio>` an, und bis der Takt es
 * merkte und `play()` kam, entstanden Lücken bis 2,9 s. Martin: beim Scrollen unterbricht es, ohne Scrollen nicht.
 *
 * Höchstens `SOFORT_MAX` Sofort-Fortsetzungen in `SOFORT_FENSTER_MS` je Lauf — sonst spielt die Seite mit iOS
 * Pingpong (anhalten, weiter, anhalten …). Darüber hinaus gilt wie bisher der Takt (`WEITER_MAX`, dann die 3-s-Regel).
 * Die Sofort-Fortsetzungen zählen NICHT gegen `WEITER_MAX`: sonst wäre der Takt nach drei Scroll-Unterbrechungen eines
 * langen Stücks verbraucht und das nächste Anhalten liefe in den frischen Anlauf.
 * ⚠️ Nie nach eigenem Anhalten (Nutzer-Pause, Sperrbildschirm-Pause, Abbruch/Barge-in/Stopp, Ende, Hinweis „Tippen“),
 *    nie bei versteckter Seite und nie, wenn beim Unterbrechen anderer Ton lief (Anruf, Musik: `audio ja`).
 */
export const SOFORT_MAX = 3;
export const SOFORT_FENSTER_MS = 5000;
/** Höchstens so viele Sofort-Fortsetzungen in EINEM Lauf, gleich wie verteilt (Prüfung 17.10.) — danach nur noch der Takt. */
export const SOFORT_JE_LAUF_MAX = 6;
/** So lange wartet das Sofort-Weiterspielen höchstens auf die Route der App (ms); ohne Antwort: nicht sofort, der Takt bleibt. */
export const ROUTE_FRIST_MS = 1000;

/** Ausgänge, die am Kopf oder im Auto hängen (`ausgabeKuerzel`, playback-hifi.ts). */
const KOPF_AUSGAENGE = new Set(["bt-a2dp", "bt-hfp", "bt-le", "kopfhoerer", "auto", "airplay"]);

/**
 * Hat das System angehalten, weil der Kopfhörer weg ist (AirPods heraus, Bluetooth getrennt)? Dann ginge der Ton beim
 * Weiterspielen über den Lautsprecher oder die Hörmuschel — das ist eine Pause, kein Unfall. (Prüfung 17.10.)
 * Ebenso, wenn der Ausgang vorher am Kopf/im Auto hing und jetzt UNBEKANNT ist (`null`: die App antwortet nicht oder
 * nicht in der Frist) — im Zweifel nicht laut weiterspielen (Prüfung 17.10., Auflage A).
 */
export function kopfhoererWeg(vorher: string | null | undefined, jetzt: string | null): boolean {
  return !!vorher && KOPF_AUSGAENGE.has(vorher) && (jetzt === null || jetzt === "lautsprecher" || jetzt === "hoerer");
}

/** Darf jetzt noch sofort fortgesetzt werden? Rechnet nur (`zeiten`: die bisherigen Sofort-Fortsetzungen dieses Laufs). */
export function sofortErlaubt(zeiten: readonly number[], jetzt: number): boolean {
  if (zeiten.length >= SOFORT_JE_LAUF_MAX) return false;
  return zeiten.filter((t) => jetzt - t < SOFORT_FENSTER_MS).length < SOFORT_MAX;
}

/**
 * So weit muss die Zeit vorrücken, damit ein Stück nicht mehr „am Anfang steht“ (Sekunden) — derselbe Wert wie in
 * den Prüfungen von `playOne` (Startmessung, Rückkehr, Bilanz).
 */
export const FORTGANG_MIN_S = 0.05;

/**
 * Ab so viel gehörtem Ton läuft ein Stück an der erreichten Stelle weiter statt von seinem Anfang. Darunter ist es
 * höchstens eine angeschnittene Silbe — die wird lieber ganz noch einmal gesprochen als mitten im Wort fortgesetzt.
 * Dieselbe halbe Sekunde muss die Zeit vorrücken, bevor „Tippen zum Weiterspielen“ wieder geht (datei-spieler.ts).
 */
export const GEHOERT_AB_S = 0.5;

/** Gesprochen wird mit rund 15,5 Zeichen je Sekunde (Messung an `FIRST_SPEAKABLE_MIN`, tts-queue.ts). */
const ZEICHEN_JE_S = 15.5;

/**
 * So nah an seinem Ende gilt ein Stück als ganz gehört (Sekunden). iOS verliert gelegentlich das `ended`
 * (tts-queue.ts, Sonde vom 19.07.): das Element steht dann kurz vor seiner Dauer, bis der Wachhund es beendet —
 * zu hören war alles. Ohne diese Grenze stünden dafür ein, zwei Zeichen als „verloren“ in der Bilanz.
 */
export const ENDE_NAH_S = 0.25;

/**
 * So lange wartet „Tippen zum Weiterspielen“ bei SICHTBARER Seite auf eine Berührung (ms); danach endet das Vorlesen
 * mit dem Abbruch `kein-tipp`. Eine Minute: wer hinsieht, tippt in Sekunden; wer nicht hinsieht, bekommt nach einer
 * Minute Tagesaufnahme und Wach-Wort zurück. Zeit mit versteckter Seite oder in einer Nutzer-Pause zählt nicht.
 */
export const HINWEIS_WARTET_MAX_MS = 60_000;

/** Ist der Wiederanlauf eingeschaltet? Vorgabe: ja. Ohne localStorage (Privatmodus, Tests ohne DOM): ja. */
export function wiederanlaufAn(): boolean {
  try {
    return localStorage.getItem(WIEDERANLAUF_SCHLUESSEL) !== "0";
  } catch {
    return true;
  }
}

/** Den Schalter setzen (Einstellungen → Stimme → Schneller Ton). Wirkt beim nächsten Stück, ohne Neuladen. */
export function setzeWiederanlauf(an: boolean): void {
  try {
    if (an) localStorage.removeItem(WIEDERANLAUF_SCHLUESSEL);
    else localStorage.setItem(WIEDERANLAUF_SCHLUESSEL, "0");
  } catch {
    /* ohne Speicher gilt die Vorgabe */
  }
}

/** Die Startstelle aus der Adresse (`…#t=4.36`), sonst 0. Dorthin springt das Element nach dem Laden von selbst. */
export function startAusAdresse(url: string): number {
  const m = /#(?:.*&)?t=(\d+(?:\.\d+)?)/.exec(url);
  const s = m ? Number(m[1]) : 0;
  return Number.isFinite(s) && s > 0 ? s : 0;
}

/** Dieselbe Adresse, ab `abS` Sekunden (0 = ohne Anker, also von vorn). */
export function adresseAb(url: string, abS: number): string {
  const ohne = url.split("#", 1)[0] ?? url;
  return abS > 0 ? `${ohne}#t=${abS.toFixed(2)}` : ohne;
}

/**
 * Wo der Wiederanlauf beginnt: an der Stelle, an der das Element stand, wenn seit `basisS` (Anfang dieses Laufs)
 * mindestens `GEHOERT_AB_S` zu hören waren — sonst wieder bei `basisS`.
 */
export function wiederanlaufStelle(standS: number, basisS: number): number {
  const stand = Number.isFinite(standS) && standS > 0 ? standS : 0;
  const basis = Number.isFinite(basisS) && basisS > 0 ? basisS : 0;
  return stand - basis >= GEHOERT_AB_S ? stand : basis;
}

/**
 * Die Adresse, mit der ein stehendes Stück frisch anläuft.
 *
 * Vom Server geholte Adressen beginnen an der erreichten Stelle (`#t=`): das ist am iPhone belegt — Journal 02.10.
 * 16:24 (`datei … ab=8.55s`, danach `via=ended audio=8.97s echt=0.88s`), 03.10. 09:06 und 04.10. 13:52.
 *
 * ⚠️ Eine `blob:`-Adresse (der Weg ohne PCM- und Strom-Schalter) hat am iPhone noch NIE mit Anker gespielt. In WebKit
 *    auf Linux springt sie an die Stelle (tests/browser/vorlesen-haengt-webkit.mts, Ablauf 7 misst es mit), aber dort
 *    lädt ein anderer Unterbau als am iPhone. Sie läuft darum wie bisher von ihrem Anfang an: lieber ein paar
 *    Sekunden doppelt als ein Stück, das gar nicht lädt.
 */
export function wiederanlaufAdresse(quelle: string, standS: number, basisS: number): string {
  const basis = Number.isFinite(basisS) && basisS > 0 ? basisS : 0;
  const stelle = quelle.startsWith("blob:") ? basis : wiederanlaufStelle(standS, basis);
  return adresseAb(quelle, stelle);
}

/**
 * Beginnt die Zählung der Anläufe neu, wenn das Stück jetzt wieder steht? (Prüfung 06.10.2026)
 *
 * Ja, wenn der laufende Anlauf hörbar (`GEHOERT_AB_S`) über die höchste Stelle hinauskam, die das Stück vorher
 * erreicht hatte (`hoechsteS`) — dann hat der letzte Anlauf geholfen, und der neue Hänger ist ein neuer Fall. Bis
 * dahin zählte jeder Anlauf: drei kurze Hänger in EINEM Stück (WebKit-Probe der Prüfung, ein 12-s-Stück, dreimal
 * 0,3 s versteckt) endeten in „Tippen zum Weiterspielen“ und `kein-tipp`, wo main das Stück noch ganz sprach.
 *
 * ⚠️ Nie bei einer `blob:`-Adresse: sie läuft von ihrem Anfang an (`wiederanlaufAdresse`), spricht also Gehörtes
 *    noch einmal. Dort zählt weiter jeder Anlauf — sonst begänne ein Stück, das immer wieder ein Stück weiter kommt
 *    und stehen bleibt, ohne Ende von vorn (ein Kreis, in dem der Anfang wieder und wieder zu hören ist).
 */
export function anlaufZaehltNeu(quelle: string, standS: number, hoechsteS: number): boolean {
  if (quelle.startsWith("blob:")) return false;
  const stand = Number.isFinite(standS) && standS > 0 ? standS : 0;
  const hoechste = Number.isFinite(hoechsteS) && hoechsteS > 0 ? hoechsteS : 0;
  return stand - hoechste >= GEHOERT_AB_S;
}

/** Wie weit ein Stück gekommen ist — geschrieben von dem Spieler, der es gerade trägt (Datei, PCM, nativ). */
export interface StueckStand {
  /** Bis zu dieser Stelle (Sekunden im Stück) rückte die Zeit vor. */
  bisS: number;
  /** Länge des Stücks in Sekunden, NaN solange unbekannt. */
  dauerS: number;
  /** Bis zum Ende gespielt (`ended`, PCM-Ende)? */
  ganz: boolean;
}

/**
 * Wie viele Zeichen eines Stücks wirklich zu hören waren. (Bilanz `[sprech-d] wiedergabe … gesprochen=`)
 *
 * Bis 05.10. zählte ein Stück ganz oder gar nicht: abgebrochen = 0 Zeichen (auch nach fünf gehörten Sekunden),
 * vom Wachhund nach 14 s Stille übersprungen = alle Zeichen. Jetzt zählt der Anteil der Zeit, die vorrückte.
 * Ohne bekannte Dauer wird über das Sprechtempo geschätzt.
 */
export function gesprocheneZeichen(laenge: number, stand: StueckStand): number {
  if (laenge <= 0) return 0;
  if (stand.ganz) return laenge;
  const bis = Number.isFinite(stand.bisS) && stand.bisS > 0 ? stand.bisS : 0;
  if (bis <= 0) return 0;
  const dauer = Number.isFinite(stand.dauerS) && stand.dauerS > 0 ? stand.dauerS : 0;
  // Bis kurz vor das Ende gekommen (das `ended` ging verloren): gehört wurde alles.
  if (dauer > 0 && bis >= dauer - ENDE_NAH_S) return laenge;
  const anteil = dauer > 0 ? bis / dauer : (bis * ZEICHEN_JE_S) / laenge;
  return Math.max(0, Math.min(laenge, Math.round(laenge * anteil)));
}
